import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SITE } from "../../../lib/siteData";
import { fadeUp } from "../../../lib/motion";

export default function WhySection() {
  return (
    <section
      id="why"
      className="relative py-24 sm:py-32 bg-white"
      data-testid="why-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-[12px] uppercase tracking-[0.36em] text-[#011233] font-semibold">
            Why Knight
          </p>
          <h2 className="mt-5 font-heading font-bold text-[#011233] text-4xl sm:text-6xl leading-[1.04] tracking-tight">
            Why work with an independent agent.
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left — Our Promise card + Latin-family image */}
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="lg:sticky lg:top-32 space-y-6">
              <div className="rounded-[28px] bg-[#011233] text-white p-9 sm:p-11">
                <p className="text-[11px] uppercase tracking-[0.36em] text-[#B5BAC2] font-semibold">
                  Our promise
                </p>
                <h3 className="mt-5 font-heading font-bold text-3xl sm:text-4xl leading-[1.05]">
                  We work for you —
                  <br />
                  not the insurance
                  <br />
                  <span className="text-[#B5BAC2]">companies.</span>
                </h3>
                <p className="mt-6 text-[14px] text-white/85 leading-relaxed">
                  Knight Insurance Solutions partners with a select group of reputable
                  carriers — so we can match your needs with the company offering the
                  most comprehensive coverage at the most competitive price.
                </p>
                <a
                  href="#quote"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#011233] px-6 py-3 text-[14px] font-semibold hover:bg-[#B5BAC2] transition-all"
                  data-testid="why-cta-quote"
                >
                  Get A Quote
                  <ArrowRight size={16} />
                </a>
              </div>

              <div
                className="rounded-[28px] overflow-hidden aspect-[4/3] ring-1 ring-[#011233]/10"
                data-testid="why-promise-image"
              >
                <img
                  src="https://images.unsplash.com/photo-1611024847487-e26177381a3f?fm=jpg&q=80&w=1200&auto=format&fit=crop"
                  alt="Latin family — protected by Knight Insurance Solutions"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Right — Reasons in a dynamic alternating layout */}
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-5">
              {SITE.reasons.map((r, i) => (
                <motion.div
                  key={`${r.title}-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.04 }}
                  whileHover={{ y: -6 }}
                  className={`kn-reason group rounded-2xl p-6 transition-colors ${
                    i % 2 === 0
                      ? "bg-[#E5E5EA] hover:bg-white"
                      : "bg-white ring-1 ring-[#011233]/10 hover:bg-[#011233] hover:text-white"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading font-bold text-2xl tracking-tight text-[#B5BAC2] group-hover:text-current transition-colors tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <ArrowRight
                      size={16}
                      className="opacity-30 group-hover:opacity-100 group-hover:translate-x-1 transition-all text-current"
                    />
                  </div>
                  <h4 className="font-heading font-semibold text-base sm:text-lg tracking-tight">
                    {r.title}
                  </h4>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600 group-hover:text-current/85">
                    {r.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
